import axios from 'axios';
import { apiUrl } from './config';
import { getAuthToken, getPortalType, hasAnyStoredAuthToken } from '@/utils/auth';
import { trackEvent, attachUserProps, trackApiRequest, trackApiError } from '@/analytics/analytics';

/**
 * Chat API Service for Budlee AI Chat Feature
 * Uses /api/v1/chat/sessions/ endpoints
 */

const HANDLED_LIMIT_MESSAGE = 'HANDLED_LIMIT';
const LIMIT_ERROR_CODES = new Set(['limit_exceeded', 'payment_required']);
const isBrowser = typeof window !== 'undefined';

const isGuestSession = () => {
  if (!isBrowser) return false;
  try {
    if (localStorage.getItem('is_guest_session') === 'true') return true;
    return !hasAnyStoredAuthToken();
  } catch (error) {
    return false;
  }
};

const normalizeLimitData = (data = {}) => {
  if (!data) return {};
  if (typeof data === 'object') return data;
  return { message: String(data) };
};

const createHandledLimitError = (statusCode, data = {}) => {
  const normalizedData = normalizeLimitData(data);
  const error = new Error(HANDLED_LIMIT_MESSAGE);
  error.code = HANDLED_LIMIT_MESSAGE;
  error.handledLimit = true;
  error.status = statusCode;
  error.data = normalizedData;
  error.response = { status: statusCode, data: normalizedData };
  return error;
};

export const isHandledLimitError = (error) => Boolean(
  error?.handledLimit ||
  error?.code === HANDLED_LIMIT_MESSAGE ||
  error?.message === HANDLED_LIMIT_MESSAGE ||
  error?.data?.message === HANDLED_LIMIT_MESSAGE ||
  error?.response?.data?.message === HANDLED_LIMIT_MESSAGE
);

const dispatchLimitModal = (statusCode, data = {}) => {
  if (!isBrowser || (statusCode !== 402 && statusCode !== 429)) return;

  const normalizedData = normalizeLimitData(data);
  const guest = isGuestSession();
  const userType = normalizedData?.user_type || (guest ? 'guest' : 'free');
  window.dispatchEvent(new CustomEvent('paymentRequired', {
    detail: {
      feature: normalizedData?.feature || 'CHAT',
      message: normalizedData?.message || (guest
        ? 'You have already hit your guest account limits. Sign up for more limits.'
        : 'You have already hit your free account limits'),
      remaining: normalizedData?.remaining,
      max_count: normalizedData?.max_count,
      statusCode,
      user_type: userType,
      isFreeUser: userType === 'free' || !guest,
    }
  }));
};

const resolveLimitStatus = (statusCode, data = {}) => {
  const numericStatus = Number(statusCode || 0);
  if (numericStatus === 402 || numericStatus === 429) return numericStatus;

  const normalizedData = normalizeLimitData(data);
  const code = String(normalizedData?.error || normalizedData?.code || '').toLowerCase();
  return LIMIT_ERROR_CODES.has(code) ? 402 : null;
};

const throwIfLimitResponse = (statusCode, data = {}) => {
  const limitStatus = resolveLimitStatus(statusCode, data);
  if (!limitStatus) return;
  dispatchLimitModal(limitStatus, data);
  throw createHandledLimitError(limitStatus, data);
};

const throwIfHandledLimitPayload = (data = {}) => {
  if (data?.message !== HANDLED_LIMIT_MESSAGE) return;
  const statusCode = Number(data?.statusCode || data?.status_code || data?.status || 402);
  throw createHandledLimitError(statusCode, data);
};

const unwrapChatResponse = (response) => {
  throwIfHandledLimitPayload(response?.data);
  return response.data;
};

const readResponseData = async (response) => {
  const text = await response.text();
  if (!text) return { data: null, detail: '' };
  try {
    const data = JSON.parse(text);
    return {
      data,
      detail: data?.message || data?.error || JSON.stringify(data),
    };
  } catch (error) {
    return { data: text, detail: text };
  }
};

/**
 * Create a new chat session
 * @param {Object} params - Session parameters
 * @param {number} params.subject_id - Subject ID (optional)
 * @param {number} params.chapter_id - Chapter ID (optional)
 * @param {string} params.page_context - Page context (chat, learn, curiosity, etc.)
 * @param {string} params.explanation_mode - Explanation mode (textbook, detailed, beyond_book) - only for 'chat' context
 * @param {number} params.curiosity_content_id - Content ID for curiosity contexts (optional)
 * @param {number} params.topic_id - Topic ID (optional)
 * @returns {Promise<Object>} Session object with session_id
 * @throws {Error} Throws error with status 429 if rate limit exceeded
 */
export const createChatSession = async (params) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);
  const currentLang = (typeof document !== 'undefined' && document.documentElement.lang) || (typeof localStorage !== 'undefined' && localStorage.getItem('app-language')) || 'en';

  // Build headers - include auth token if available (for guests, it will be undefined)
  const headers = {
    'Content-Type': 'application/json',
    'Accept-Language': currentLang,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const sessionParams = { ...params };
  if (currentLang === 'ar' && !sessionParams.language) {
    sessionParams.language = 'ar';
  }

  try {
    const response = await axios.post(
      apiUrl('v1/chat/sessions/'),
      sessionParams,
      { headers }
    );
    return unwrapChatResponse(response);
  } catch (error) {
    if (isHandledLimitError(error)) throw error;
    throwIfLimitResponse(error.response?.status, error.response?.data);
    throw error;
  }
};

/**
 * Send a message in a chat session
 * @param {string} sessionId - Session UUID
 * @param {string} content - Message content
 * @param {AbortSignal} signal - Optional AbortSignal for cancelling the request
 * @param {number} teacherNoteId - Optional teacher note ID for class notes context
 * @param {Object} anchor - Optional selection anchor metadata (e.g., from PDF selection)
 * @returns {Promise<Object>} Message object with answer, key_points, suggested_questions, etc.
 * @throws {Error} Throws error with status 429 if rate limit exceeded
 */
// NOTE: `studentClass` is optional and is interpreted as grade/class level (1-12).
export const sendChatMessage = async (
  sessionId,
  content,
  signal = null,
  teacherNoteId = null,
  anchor = null,
  explanationMode = null,
  studentClass = null,
  isGeneralChat = false
) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);
  const currentLang = (typeof document !== 'undefined' && document.documentElement.lang) || (typeof localStorage !== 'undefined' && localStorage.getItem('app-language')) || 'en';

  // Build payload with content and optional teacher_note_id
  const payload = { content };
  if (currentLang === 'ar') {
    payload.language = 'ar';
  }
  if (teacherNoteId !== null) {
    payload.teacher_note_id = teacherNoteId;
  }
  if (anchor !== null && anchor !== undefined) {
    payload.anchor = anchor;
  }
  if (explanationMode) {
    payload.explanation_mode = explanationMode;
  }
  if (studentClass !== null && studentClass !== undefined && studentClass !== '') {
    payload.student_class = Number(studentClass);
  }
  if (isGeneralChat) {
    payload.is_general_chat = isGeneralChat;
  }

  // Build config with headers and optional abort signal
  const headers = {
    'Content-Type': 'application/json',
    'Accept-Language': currentLang,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const config = {
    headers
  };

  // Add abort signal if provided
  if (signal) {
    config.signal = signal;
  }

  try {
    const response = await axios.post(
      apiUrl(`v1/chat/sessions/${sessionId}/messages/`),
      payload,
      config
    );
    return unwrapChatResponse(response);
  } catch (error) {
    if (isHandledLimitError(error)) throw error;
    throwIfLimitResponse(error.response?.status, error.response?.data);
    throw error;
  }
};

/**
 * Send a message in a chat session with streaming (SSE).
 * Backend: POST /v1/chat/sessions/:id/messages/?stream=1 with `text/event-stream` response.
 *
 * Emits events:
 * - `delta`: { text }
 * - `mode`: { source_type, display_mode, no_textbook_result, auto_switch_to_mode }
 * - `final`: full message payload (same as non-stream endpoint)
 * - `cancel`: { status, detail }
 * - `error`: { message }
 */
export const sendChatMessageStream = async (
  sessionId,
  payload,
  {
    signal = null,
    onDelta = null,
    onReplace = null,
    onEvent = null,
  } = {}
) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);
  const startedAt = Date.now();
  const streamUrl = apiUrl(`v1/chat/sessions/${sessionId}/messages/?stream=1`);
  const currentLang = (typeof document !== 'undefined' && document.documentElement.lang) || (typeof localStorage !== 'undefined' && localStorage.getItem('app-language')) || 'en';

  const headers = {
    'Content-Type': 'application/json',
    'Accept-Language': currentLang,
    Accept: 'text/event-stream'
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const streamPayload = { ...payload };
  if (currentLang === 'ar' && !streamPayload.language) {
    streamPayload.language = 'ar';
  }

  const res = await fetch(streamUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify(streamPayload),
    signal,
  });

  if (!res.ok) {
    let detail = '';
    let responseData = null;
    try {
      const parsed = await readResponseData(res);
      detail = parsed.detail;
      responseData = parsed.data;
    } catch (e) {}
    throwIfLimitResponse(res.status, responseData);
    try {
      trackApiError(attachUserProps({
        page: 'chat',
        api_method: 'POST',
        api_url: streamUrl,
        status: res.status,
        duration_ms: Math.max(0, Date.now() - startedAt),
        api_transport: 'fetch_stream',
        error: detail || `Streaming request failed (${res.status})`,
      }));
    } catch {}
    const err = new Error(detail || `Streaming request failed (${res.status})`);
    err.status = res.status;
    err.response = { status: res.status, data: responseData };
    throw err;
  }

  const contentType = (res.headers.get('content-type') || '').toLowerCase();
  if (!contentType.includes('text/event-stream')) {
    // Fallback: server didn't stream; try to parse JSON.
    const data = await res.json();
    return data;
  }

  if (!res.body) {
    throw new Error('Streaming not supported (no response body)');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  const emit = (eventName, data) => {
    try {
      onEvent && onEvent(eventName, data);
    } catch (e) {}
    if (eventName === 'delta') {
      try {
        onDelta && onDelta(data);
      } catch (e) {}
    }
    if (eventName === 'replace') {
      try {
        onReplace && onReplace(data);
      } catch (e) {}
    }
  };

  const parseEventBlock = (block) => {
    const lines = block.split('\n');
    let eventName = 'message';
    let dataLines = [];
    for (const line of lines) {
      if (!line) continue;
      if (line.startsWith(':')) continue; // comment
      if (line.startsWith('event:')) {
        eventName = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).trimStart());
      }
    }
    const dataStr = dataLines.join('\n');
    if (!dataStr) return null;
    let data;
    try {
      data = JSON.parse(dataStr);
    } catch (e) {
      data = { raw: dataStr };
    }
    return { eventName, data };
  };

  let finalPayload = null;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let idx;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);

      const parsed = parseEventBlock(block);
      if (!parsed) continue;

      emit(parsed.eventName, parsed.data);
      if (parsed.eventName === 'final') {
        finalPayload = parsed.data;
      }
      if (parsed.eventName === 'error') {
        const statusCode = Number(parsed.data?.status || parsed.data?.status_code || parsed.data?.statusCode || 0);
        const code = String(parsed.data?.error || parsed.data?.code || '').toLowerCase();
        if (statusCode === 402 || statusCode === 429 || code === 'limit_exceeded' || code === 'payment_required') {
          throwIfLimitResponse(statusCode || 402, parsed.data);
        }
        const err = new Error(parsed.data?.message || 'Streaming error');
        err.data = parsed.data;
        throw err;
      }
      if (parsed.eventName === 'cancel') {
        const err = new Error(parsed.data?.detail || 'Generation cancelled');
        err.code = 'CANCELLED';
        err.data = parsed.data;
        throw err;
      }
    }
  }

  // If we never saw a final event, return null to let caller fall back.
  try {
    trackApiRequest(attachUserProps({
      page: 'chat',
      api_method: 'POST',
      api_url: streamUrl,
      status: 200,
      duration_ms: Math.max(0, Date.now() - startedAt),
      api_transport: 'fetch_stream',
    }));
  } catch {}
  throwIfHandledLimitPayload(finalPayload);
  return finalPayload;
};

/**
 * Cancel chat generation and stop LLM processing
 * @param {string} sessionId - Session UUID
 * @returns {Promise<Object>} Status object
 */
export const cancelChatGeneration = async (sessionId) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  const headers = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await axios.post(
    apiUrl(`v1/chat/sessions/${sessionId}/cancel/`),
    {},
    {
      headers
    }
  );

  return unwrapChatResponse(response);
};

/**
 * List chat sessions
 * @param {Object} params - Query parameters
 * @param {string} params.page_context - Filter by page context (optional)
 * @param {number} params.page - Page number (optional)
 * @param {number} params.page_size - Page size (optional)
 * @returns {Promise<Object>} Paginated list of sessions
 */
export const listChatSessions = async (params = {}) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  const headers = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await axios.get(
      apiUrl('v1/chat/sessions/'),
      {
        params,
        headers
      }
    );

    return unwrapChatResponse(response);
  } catch (error) {
    if (isHandledLimitError(error)) throw error;
    throwIfLimitResponse(error.response?.status, error.response?.data);
    throw error;
  }
};

/**
 * Get a chat session with all messages
 * @param {string} sessionId - Session UUID
 * @returns {Promise<Object>} Session object with messages array, or null if session not found (guest session expired)
 */
export const getChatSession = async (sessionId) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  const headers = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await axios.get(
      apiUrl(`v1/chat/sessions/${sessionId}/`),
      { headers }
    );
    return unwrapChatResponse(response);
  } catch (error) {
    if (isHandledLimitError(error)) throw error;
    throwIfLimitResponse(error.response?.status, error.response?.data);
    // For guest users (no token), 404 likely means the session cache expired
    // Return null and let the frontend create a new session
    if (!token && error.response?.status === 404) {
      console.warn('Guest session not found - likely expired. Creating new session.');
      try {
        trackEvent('guest_session_expired', attachUserProps({
          page: 'chat',
          session_id: sessionId,
          source: 'chat_session_retrieve',
        }));
      } catch {}
      return null;
    }
    throw error;
  }
};

/**
 * Get or create a session for a specific context
 * Helper function that checks for existing active session or creates a new one
 * @param {Object} params - Session parameters (same as createChatSession)
 * @param {boolean} createNew - Force create new session (default: false)
 * @returns {Promise<Object>} Session object
 */
export const getOrCreateSession = async (params, createNew = false) => {
  if (createNew) {
    return createChatSession(params);
  }

  // Try to find existing active session with same context
  try {
    const queryParams = {
      page_context: params.page_context,
      page_size: 100  // Get more to filter properly
    };

    const sessions = await listChatSessions(queryParams);

    if (sessions.results && sessions.results.length > 0) {
      // Filter for matching context based on page_context
      let activeSession = null;

      if (params.page_context === 'chat' || params.page_context === 'learn') {
        // For chat/learn contexts, match subject_id and chapter_id
        activeSession = sessions.results.find(s =>
          s.is_active &&
          s.subject_id === params.subject_id &&
          s.chapter_id === params.chapter_id
        );
      } else if (params.page_context === 'learn_budlee_card') {
        // Scoped Learn chats, including Class Notes, need their own thread.
        // Class Notes use curiosity_content_id as the scoped source id to avoid
        // a ChatSession schema migration; older card-scoped asks can still fall
        // back to the stable title.
        activeSession = sessions.results.find(s => {
          if (!s.is_active || s.page_context !== 'learn_budlee_card') return false;
          if (s.subject_id !== params.subject_id || s.chapter_id !== params.chapter_id) return false;
          if (params.curiosity_content_id !== null && params.curiosity_content_id !== undefined) {
            return s.curiosity_content_id === params.curiosity_content_id;
          }
          return !params.title || s.title === params.title;
        });
      } else if (params.page_context === 'curiosity_video' || params.page_context === 'curiosity_podcast' || params.page_context === 'curiosity_content') {
        // For video/podcast/content contexts, match curiosity_content_id and also subject/chapter when present
        activeSession = sessions.results.find(s => {
          if (!s.is_active) return false;
          // If subject/chapter provided, match them as well to ensure correct curriculum context
          if ((params.subject_id || params.chapter_id) && (s.subject_id !== params.subject_id || s.chapter_id !== params.chapter_id)) {
            return false;
          }
          const requestedTopicId = params.topic_id ?? null;
          const sessionTopicId = s.topic_id ?? null;
          if (requestedTopicId !== sessionTopicId) {
            return false;
          }
          return s.curiosity_content_id === params.curiosity_content_id;
        });
      } else if (params.page_context === 'course_content') {
        // For course content syllabus items
        activeSession = sessions.results.find(s =>
          s.is_active &&
          s.page_context === 'course_content' &&
          s.curiosity_content_id === params.curiosity_content_id
        );
      } else if (params.page_context === 'curiosity_topic_detail') {
        // For topic detail, match topic_id
        activeSession = sessions.results.find(s =>
          s.is_active &&
          s.topic_id === params.topic_id
        );
      } else {
        // For other contexts, just check is_active
        activeSession = sessions.results.find(s => s.is_active);
      }

      if (activeSession) {
        return { session_id: activeSession.session_id, ...activeSession };
      }
    }
  } catch (error) {
    if (isHandledLimitError(error)) throw error;
    console.warn('Failed to fetch existing sessions, creating new one:', error);
  }

  // Create new session if no active session found
  return createChatSession(params);
};

/**
 * Update a chat session's explanation_mode
 * @param {string} sessionId - Session UUID
 * @param {string} explanationMode - New explanation mode (textbook, detailed, beyond_book)
 * @returns {Promise<Object>} Updated session object
 */
export const updateSessionExplanationMode = async (sessionId, explanationMode, options = {}) => {
  if (!sessionId) {
    console.warn('updateSessionExplanationMode called without sessionId');
    return null;
  }

  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  const headers = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const payload = { explanation_mode: explanationMode };

  // Auto-generate title for guest sessions if it's currently "Untitled Chat"
  if (!token && options.chapterName && options.subjectName) {
    payload.title = `${options.subjectName}: ${options.chapterName}`;
  }

  try {
    const response = await axios.patch(
      apiUrl(`v1/chat/sessions/${sessionId}/`),
      payload,
      { headers }
    );

    return unwrapChatResponse(response);
  } catch (error) {
    if (isHandledLimitError(error)) throw error;
    throwIfLimitResponse(error.response?.status, error.response?.data);
    throw error;
  }
};

export const transcribeAudio = async (audioBlob) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);
  const formData = new FormData();
  const blobType = String(audioBlob?.type || '');
  const extension = blobType.includes('mp4') ? 'm4a' : (blobType.includes('ogg') ? 'ogg' : 'webm');
  formData.append('audio', audioBlob, `budlee-voice.${extension}`);

  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await axios.post(
    apiUrl('v1/chat/voice/transcribe/'),
    formData,
    { headers }
  );
  return unwrapChatResponse(response);
};

export const connectRealtimeVoice = async (offerSdp, params = {}) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      query.set(key, String(value));
    }
  });

  const headers = {
    'Content-Type': 'application/sdp',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const url = `${apiUrl('v1/chat/voice/realtime/connect/')}${query.toString() ? `?${query.toString()}` : ''}`;
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: offerSdp,
  });

  if (!response.ok) {
    let detail = '';
    let responseData = null;
    try {
      const parsed = await readResponseData(response);
      detail = parsed.detail;
      responseData = parsed.data;
    } catch (e) {}
    throwIfLimitResponse(response.status, responseData);
    throw new Error(detail || `Realtime voice connection failed (${response.status})`);
  }

  return response.text();
};

export const synthesizeSpeech = async (text, voice = 'nova') => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(apiUrl('v1/chat/voice/synthesize/'), {
    method: 'POST',
    headers,
    body: JSON.stringify({ text, voice }),
  });

  if (!response.ok) {
    let detail = '';
    let responseData = null;
    try {
      const parsed = await readResponseData(response);
      detail = parsed.detail;
      responseData = parsed.data;
    } catch (e) {}
    throwIfLimitResponse(response.status, responseData);
    throw new Error(detail || `Speech synthesis failed (${response.status})`);
  }

  return response.blob();
};

/**
 * Fetch call info for an AI Tutor by tutor ID or subject name
 */
export const fetchTutorCallInfo = async (tutorId, subjectName = null, options = {}) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);
  const currentLang = (typeof document !== 'undefined' && document.documentElement.lang) || (typeof localStorage !== 'undefined' && localStorage.getItem('app-language')) || 'en';
  const opts = (subjectName && typeof subjectName === 'object')
    ? subjectName
    : (options || {});
  const resolvedSubjectName = (subjectName && typeof subjectName === 'object')
    ? (subjectName.subjectName || subjectName.subject_name)
    : subjectName;

  const headers = { 
    'Content-Type': 'application/json',
    'Accept-Language': currentLang,
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let url = tutorId 
    ? apiUrl(`v1/chat/voice/tutors/${tutorId}/call-info/`)
    : apiUrl('v1/chat/voice/tutors/by-subject/');

  const params = {};
  if (!tutorId && resolvedSubjectName) {
    params.subject_name = resolvedSubjectName;
  }
  const subjectId = opts.subjectId || opts.subject_id;
  const chapterId = opts.chapterId || opts.chapter_id;
  if (!tutorId && subjectId) {
    params.subject_id = subjectId;
  }
  if (chapterId) {
    params.chapter_id = chapterId;
  }
  if (opts.allowFallbackTutor || opts.allow_fallback_tutor) {
    params.allow_fallback_tutor = '1';
  }

  const response = await axios.get(url, { headers, params });
  return unwrapChatResponse(response);
};

/**
 * Log the start of an AI Tutor voice call
 */
export const startVoiceCall = async (tutorId, sessionId = null) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);
  const currentLang = (typeof document !== 'undefined' && document.documentElement.lang) || (typeof localStorage !== 'undefined' && localStorage.getItem('app-language')) || 'en';

  const headers = { 
    'Content-Type': 'application/json',
    'Accept-Language': currentLang,
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await axios.post(
    apiUrl('v1/chat/voice/calls/start/'),
    { tutor_id: tutorId, session_id: sessionId, language: currentLang },
    { headers }
  );
  return unwrapChatResponse(response);
};

/**
 * Log the end of an AI Tutor voice call
 */
export const endVoiceCall = async (sessionId) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await axios.post(
    apiUrl('v1/chat/voice/calls/end/'),
    { session_id: sessionId },
    { headers }
  );
  return unwrapChatResponse(response);
};

export const getLiveKitToken = async (roomName, context = {}) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const payload = {
    room_name: roomName,
    tutor_id: context.tutorId || context.tutor_id,
    subject_name: context.subjectName || context.subject_name,
    chapter_name: context.chapterName || context.chapter_name,
    student_class: context.studentClass || context.student_class,
    page_context: context.pageContext || context.page_context,
    chapter_id: context.chapterId || context.chapter_id,
    subject_id: context.subjectId || context.subject_id,
    allow_fallback_tutor: context.allowFallbackTutor || context.allow_fallback_tutor,
    voice_mode: context.voiceMode || context.voice_mode,
    initial_prompt: context.initialPrompt || context.initial_prompt,
    card_title: context.cardTitle || context.card_title,
    card_type: context.cardType || context.card_type,
    section_title: context.sectionTitle || context.section_title,
    section_number: context.sectionNumber || context.section_number,
    card_context: context.cardContext || context.card_context,
    lesson_manifest: context.lessonManifest || context.lesson_manifest,
    // Lets a new backend coordinate the pre-greeting TURN/TLS switch while
    // remaining compatible with older frontends during rolling deployments.
    voice_transport_capability: 'turn-tls-v1',
  };

  const response = await axios.post(
    apiUrl('v1/chat/voice/livekit/token/'),
    payload,
    { headers }
  );
  return unwrapChatResponse(response);
};

/**
 * Fetch persistent AI Tutor memory for current student
 */
export const getTutorMemory = async () => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await axios.get(apiUrl('v1/chat/voice/memory/'), { headers });
  return unwrapChatResponse(response);
};

/**
 * Update persistent AI Tutor memory for current student
 */
export const updateTutorMemory = async (memoryData) => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await axios.put(
    apiUrl('v1/chat/voice/memory/'),
    memoryData,
    { headers }
  );
  return unwrapChatResponse(response);
};
